import LocalMallIcon from "@mui/icons-material/LocalMall";
import QrCodeIcon from "@mui/icons-material/QrCode";
import CancelBackButton from "components/Buttons/CancelBackButton";
import FormHeader from "components/Form&DetailsHeader/Form&DetailsHeader";
import LoadingSpinner from "components/LoaderErrorWrapper/LoadingSpinner";
import {
  DynamicMenuItem,
  MenuItemConfig,
} from "components/Menuitem/DynamicMenuItem";
import PageContainer from "components/PageContainer/PageContainer";
import TruncatedTextCell from "components/TruncatedTextCell/TruncatedTextCell";
import { InwardType, normalizeInwardType } from "Constants/inwardTypes";
import { tableStyles } from "Constants/Style";
import { useEffect, useMemo, useState } from "react";
import { Card, Col, Row, Table } from "react-bootstrap";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate, useParams } from "react-router-dom";
import { fetchGRNDetails } from "slices/Warehouse/ItemInward2/GRN/thunk";
import BagQRPrint from "./BagQRPrint/BagQRPrint";
import CreateChildBagModal from "./ChildBag/CreateChildBagModal";
import FilePreviewModal from "./FilePreviewModal/FilePreviewModal";
import LabourInwardItemDetails from "./LabourInwardItemDetails";
import SalesOrderReturnsDetails from "./SalesOrderReturnsDetails";
import StockTransferInwardDetails from "./StockTransferInwardDetails";

const GRNViewDetails = () => {
  const navigate = useNavigate();
  const { id } = useParams<{ id: string }>();
  const dispatch = useDispatch<any>();

  // console.log("inventory_id (from route param 'id'):", id);

  const state = useSelector((state: any) => state.grn);
  const { details, loading } = state;
  const [showPreview, setShowPreview] = useState(false);

  useEffect(() => {
    // Fetch GRN details using the id from params
    if (id) {
      dispatch(fetchGRNDetails(id as string) as any);
    }
  }, [dispatch, id]);

  const [showModal, setShowModal] = useState(false);
  const [selectedItem, setSelectedItem] = useState<any>(null);
  const [showQRPrint, setShowQRPrint] = useState(false);
  const [qrPrintItem, setQrPrintItem] = useState<any>(null);

  const handleOpen = (item: any) => {
    setSelectedItem(item);
    setShowModal(true);
  };

  const handleClose = () => {
    setShowModal(false);
    setSelectedItem(null);
  };

  // Create menu items for DynamicMenuItem
  const menuItems: MenuItemConfig[] = useMemo(() => {
    const items: MenuItemConfig[] = [
      {
        icon: LocalMallIcon,
        label: "Create Child Bag",
        permission: true,
        onClick: (item: any) => {
          handleOpen(item);
        },
        color: "primary",
      },
      {
        icon: QrCodeIcon,
        label: "Print QR",
        permission: true,
        onClick: (item: any) => {
          setQrPrintItem(item);
          setShowQRPrint(true);
        },
        color: "info",
      },
    ];
    return items;
  }, []);

  const normalizedType = normalizeInwardType(state?.details?.inward_type);

  if (normalizedType === InwardType.ORDER_RETURN) {
    return <SalesOrderReturnsDetails />;
  } else if (normalizedType === InwardType.LABOUR_INWARD) {
    return <LabourInwardItemDetails />;
  } else if (normalizedType === InwardType.STOCK_TRANSFER) {
    return <StockTransferInwardDetails />;
  }

  return (
    <PageContainer>
      <FormHeader
        title="Inward Details (Supplier)"
        firstLabel="GRN"
        secondLabel="Inward Details"
        handleArrowClick={() => navigate("/warehouse-item-inward")}
      />

      <Card.Body>
        <style>
          {`
                    .sticky-column {
                      position: relative;
                    }
                    .sticky-column::before {
                      content: '';
                      position: absolute;
                      left: 0;
                      top: 0;
                      bottom: 0;
                      width: 1px;
                      background-color: #dee2e6;
                      z-index: 12;
                    }
                    thead .sticky-column::before {
                      z-index: 13;
                    }
                    .remark-cell {
                      max-width: 300px;
                      white-space: normal !important;
                      word-wrap: break-word;
                      overflow-wrap: break-word;
                    }
                  `}
        </style>

        {loading ? (
          <LoadingSpinner />
        ) : (
          <>
          <hr />
            <Row className="mb-2">
              <Col xs={12} md={6} lg={4}>
                <p className="mb-3">
                  <strong>GRN No. :</strong> {details?.grn_no || "-"}
                </p>
              </Col>
              <Col xs={12} md={6} lg={4}>
                <p className="mb-3">
                  <strong>Inward Date :</strong>{" "}
                  {details?.inward_date
                    ? new Date(details.inward_date).toISOString().split("T")[0]
                    : "-"}
                </p>
              </Col>
              <Col xs={12} md={6} lg={4}>
                <p className="mb-3">
                  <strong>Inward Type :</strong>{" "}
                  {normalizeInwardType(details?.inward_type) || "-"}
                </p>
              </Col>
              <Col xs={12} md={6} lg={4}>
                <p className="mb-3">
                  <strong>Invoice No. :</strong> {details?.invoice_no || "-"}
                </p>
              </Col>
              <Col xs={12} md={6} lg={4}>
                <p className="mb-3">
                  <strong>Supplier name :</strong>{" "}
                  {details?.supplier_details?.supplier_name ||
                    details?.supplier?.supplier_name ||
                    "-"}
                </p>
              </Col>

              <Col xs={12} md={6} lg={4}>
                <p className="mb-3">
                  <strong>Received By :</strong>{" "}
                  {details?.received_by_details
                    ? details.received_by_details.first_name &&
                      details.received_by_details.last_name
                      ? `${details.received_by_details.first_name} ${details.received_by_details.last_name}`
                      : details.received_by_details.username || "-"
                    : details?.received_by_user
                    ? details.received_by_user.first_name &&
                      details.received_by_user.last_name
                      ? `${details.received_by_user.first_name} ${details.received_by_user.last_name}`
                      : details.received_by_user.username || "-"
                    : "-"}
                </p>
              </Col>

              <Col xs={12} md={6} lg={4}>
                <p className="mb-3">
                  <strong>Warehouse :</strong>{" "}
                  {details?.warehouse_details?.warehouse_name ||
                    details?.warehouse?.warehouse_name ||
                    "-"}
                </p>
              </Col>
              <Col xs={12} md={6} lg={4}>
                <p className="mb-3">
                  <strong>Remark :</strong> {details?.remarks || "-"}
                </p>
              </Col>
              <Col xs={12} md={6} lg={4}>
                <p className="mb-3">
                  <strong>Document :</strong>{" "}
                  {details?.document_file ? (
                    <button
                      onClick={() => setShowPreview(true)}
                      style={{
                        color: "blue",
                        cursor: "pointer",
                        background: "none",
                        border: "none",
                        padding: 0,
                        textDecoration: "underline",
                      }}
                    >
                      View
                    </button>
                  ) : (
                    "-"
                  )}
                </p>
              </Col>

              {/* Document Viewer Popup */}

              <FilePreviewModal
                show={showPreview}
                onClose={() => setShowPreview(false)}
                url={details?.document_file}
              />
            </Row>

            <div className="mb-3 mt-3" style={{ overflowX: "auto" }}>
              <Table hover responsive className="align-middle text-center">
                <thead>
                  <tr>
                    <th
                      style={{
                        ...tableStyles.headerAlt,
                        minWidth: "70px",
                      }}
                    >
                      Sr No.
                    </th>
                    <th
                      style={{
                        ...tableStyles.headerAlt,
                        minWidth: "100px",
                      }}
                    >
                      Item Name
                    </th>
                    <th
                      style={{
                        ...tableStyles.headerAlt,
                        minWidth: "100px",
                      }}
                    >
                      Item Code
                    </th>
                    <th
                      style={{
                        ...tableStyles.headerAlt,
                        minWidth: "100px",
                      }}
                    >
                      Category
                    </th>
                    <th
                      style={{
                        ...tableStyles.headerAlt,
                        minWidth: "150px",
                      }}
                    >
                      Sub Category
                    </th>
                    <th
                      style={{
                        ...tableStyles.headerAlt,
                        minWidth: "100px",
                      }}
                    >
                      Unit
                    </th>
                    <th
                      style={{
                        ...tableStyles.headerAlt,
                        minWidth: "150px",
                      }}
                    >
                      Received Quantity
                    </th>
                    <th
                      style={{
                        ...tableStyles.headerAlt,
                        minWidth: "150px",
                      }}
                    >
                      Reserved Quantity
                    </th>
                    <th
                      style={{
                        ...tableStyles.headerAlt,
                        minWidth: "150px",
                      }}
                    >
                      Available Quantity
                    </th>
                    <th
                      style={{
                        ...tableStyles.headerAlt,
                        minWidth: "150px",
                      }}
                    >
                      Bag No.
                    </th>
                    <th
                      style={{
                        ...tableStyles.headerAlt,
                        minWidth: "100px",
                      }}
                    >
                      Rate
                    </th>
                    <th
                      style={{
                        ...tableStyles.headerAlt,
                        minWidth: "100px",
                      }}
                    >
                      Amount
                    </th>
                    <th
                      style={{
                        ...tableStyles.headerAlt,
                        minWidth: "100px",
                      }}
                    >
                      Remark
                    </th>
                    <th
                      className="sticky-column"
                      style={{
                        ...tableStyles.headerAlt,
                        ...tableStyles.actionHeader,
                        minWidth: "100px",
                      }}
                    >
                      Action
                    </th>
                  </tr>
                </thead>

                <tbody>
                  {details?.inventory_items &&
                  details.inventory_items.length > 0 ? (
                    details.inventory_items.map((item: any, idx: number) => (
                      <tr key={item.inventory_item_id || idx}>
                       <td style={tableStyles.cell}>{idx + 1}</td>
                        <td style={tableStyles.cell}>
                          {item.master_item?.item_name || "-"}
                        </td>
                        <td style={tableStyles.cell}>
                          {item.master_item?.item_code || "-"}
                        </td>
                        <td style={tableStyles.cell}>
                          {item.master_item?.category?.category_name || "-"}
                        </td>
                        <td style={tableStyles.cell}>
                          {item.master_item?.sub_category?.sub_category_name ||
                            "-"}
                        </td>
                        <td style={tableStyles.cell}>
                          {item.master_item?.unit?.unit_name || "-"}
                        </td>
                        <td style={tableStyles.cell}>{item.quantity || "-"}</td>
                        <td style={tableStyles.cell}>
                          {item.reserved_quantity || "-"}
                        </td>
                        <td style={tableStyles.cell}>
                          {item.available_quantity || "-"}
                        </td>
                        <td style={tableStyles.cell}>{item.bag_no || "-"}</td>
                        <td style={tableStyles.cell}>
                          {item.selling_price || "-"}
                        </td>
                        <td style={tableStyles.cell}>
                          {item.selling_price * item.quantity || "-"}
                        </td>
                        <td className="remark-cell" style={tableStyles.cell}>
                          <TruncatedTextCell
                            text={item.remarks || item.remark}
                            maxWidth="300px"
                            maxLines={4}
                            rowIndex={idx}
                            fallbackText="-"
                          />
                        </td>
                        <td
                          className="sticky-column"
                          style={tableStyles.actionCell}
                        >
                          <div style={tableStyles.actionButtonContainer}>
                            <DynamicMenuItem row={item} menuItems={menuItems} />
                          </div>
                        </td>
                      </tr>
                    ))
                  ) : (
                    <tr>
                      <td
                        colSpan={12}
                        className="text-center py-4"
                        style={tableStyles.cell}
                      >
                        <div className="d-flex flex-column align-items-center">
                          <i className="bi bi-inbox fs-1 text-muted mb-2"></i>
                          <p className="text-muted mb-0">No items found</p>
                        </div>
                      </td>
                    </tr>
                  )}
                </tbody>
              </Table>
            </div>
            <div className="d-flex justify-content-center gap-3 py-3">
              <CancelBackButton to="/warehouse-item-inward" />
            </div>

            <CreateChildBagModal
              key={"createChildBag"}
              show={showModal}
              handleClose={handleClose}
              selectedItem={selectedItem}
              inventoryId={id}
              onSuccess={() => {
                // Refresh GRN details after successful bag creation
                if (id) {
                  dispatch(fetchGRNDetails(id as string) as any);
                }
              }}
            />

            <BagQRPrint
              show={showQRPrint}
              handleClose={() => {
                setShowQRPrint(false);
                setQrPrintItem(null);
              }}
              inventoryItemId={qrPrintItem?.inventory_item_id || ""}
              bagNo={qrPrintItem?.bag_no || ""}
              autoPrint={true}
            />
          </>
        )}
      </Card.Body>
    </PageContainer>
  );
};

export default GRNViewDetails;
